import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api";
import { CalendarDays, MapPin, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { catMeta, CATEGORY_HERO } from "../lib/categories";
import { applyMeta } from "../lib/meta";
import { useCityGeo } from "../lib/useCityGeo";

const dayLabel = (iso) => {
  const d = new Date(iso.length <= 10 ? iso + "T00:00" : iso);
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
};
const timeLabel = (iso) => {
  if (!iso || iso.length <= 10) return "";
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

export default function CategoryBrowse() {
  const { category } = useParams();
  const cat = decodeURIComponent(category || "");
  const [events, setEvents] = useState(null);

  useEffect(() => {
    applyMeta({
      title: `${cat} events · TicketsPro.app`,
      description: `Discover and book upcoming ${cat} events on TicketsPro. Browse dates, venues and ticket prices, and buy securely in seconds.`,
      image: CATEGORY_HERO[cat] || undefined,
    });
    api.get(`/events/browse?category=${encodeURIComponent(cat)}`).then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  }, [cat]);

  const cm = catMeta(cat);
  const CIcon = cm.icon;
  const hero = CATEGORY_HERO[cat];

  const cityNames = useMemo(() => events ? [...new Set(events.map((e) => e.city).filter(Boolean))].sort() : [], [events]);
  const { city, setCity, nearName } = useCityGeo(cityNames);
  const [venue, setVenue] = useState("all");
  const venueNames = useMemo(() => {
    if (!events) return [];
    const scope = city === "all" ? events : events.filter((e) => (e.city || "") === city);
    return [...new Set(scope.map((e) => e.venue || e.venue_name).filter(Boolean))].sort();
  }, [events, city]);
  useEffect(() => { setVenue("all"); }, [city]);
  const filtered = (events || []).filter((e) =>
    (city === "all" || (e.city || "") === city) &&
    (venue === "all" || (e.venue || e.venue_name || "") === venue));

  const groups = {};
  filtered.forEach((e) => {
    const key = (e.event_date || "").slice(0, 10) || "tbc";
    (groups[key] = groups[key] || []).push(e);
  });
  const dates = Object.keys(groups).sort();

  return (
    <div className="min-h-screen bg-[#0d1626]" data-testid="category-browse">
      <div className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-white/80 hover:text-white flex items-center gap-1.5 text-sm font-medium"><ArrowLeft size={16} /> Home</Link>
          <span className="text-white font-bold">TicketsPro.app</span>
        </div>
      </div>

      <div className="relative overflow-hidden border-b border-white/10" data-testid="category-hero"
        style={hero ? {} : { background: `linear-gradient(135deg, ${cm.color}33, #0d1626)` }}>
        {hero && <img src={hero} alt={cat} className="absolute inset-0 w-full h-full object-cover opacity-40" />}
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, #0d1626ee, #0d1626aa 60%, ${cm.color}22)` }} />
        <div className="relative max-w-5xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: cm.color + "33", color: cm.color }}><CIcon size={24} /></span>
            <div>
              <p className="text-sm uppercase tracking-wide" style={{ color: cm.color }}>Browse by category</p>
              <h1 className="text-3xl sm:text-5xl font-bold text-white">{cat} events</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">

        {cityNames.length > 0 && (
          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-3">
              <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="category-city"
                className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none focus:border-[#2ec9c9] transition-colors">
                <option value="all" className="text-[#16243b]">All cities</option>
                {cityNames.map((c) => <option key={c} value={c} className="text-[#16243b]">{c}</option>)}
              </select>
              {venueNames.length > 0 && (
                <select value={venue} onChange={(e) => setVenue(e.target.value)} data-testid="category-venue"
                  className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none focus:border-[#2ec9c9] transition-colors">
                  <option value="all" className="text-[#16243b]">All venues</option>
                  {venueNames.map((v) => <option key={v} value={v} className="text-[#16243b]">{v}</option>)}
                </select>
              )}
            </div>
            {nearName && city === nearName && (
              <p className="text-xs text-[#2ec9c9] mt-3 flex items-center gap-1.5" data-testid="category-near-hint">
                <MapPin size={13} /> Showing {cat} events near you in {nearName}.{" "}
                <button onClick={() => setCity("all")} className="underline hover:text-white transition-colors">Show all cities</button>
              </p>
            )}
          </div>
        )}

        {events === null ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={32} /></div>
        ) : dates.length === 0 ? (
          <div className="py-20 text-center" data-testid="category-empty">
            <p className="text-[#8a97a8]">No upcoming {cat} events right now.</p>
            <Link to="/" className="inline-block mt-4 text-[#2ec9c9] hover:underline">Back to homepage</Link>
          </div>
        ) : (
          <div className="space-y-10">
            {dates.map((date) => (
              <div key={date} data-testid={`browse-day-${date}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2 text-white font-semibold">
                    <CalendarDays size={18} className="text-[#2ec9c9]" />
                    <span>{date === "tbc" ? "Date to be confirmed" : dayLabel(date)}</span>
                  </div>
                  <span className="text-xs text-[#8a97a8] bg-white/5 rounded-full px-2.5 py-1">{groups[date].length} event{groups[date].length !== 1 ? "s" : ""}</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {groups[date].map((e) => (
                    <a key={e.id} href={`/store/${e.business_slug}/events/${e.slug}`} data-testid={`browse-card-${e.id}`}
                      className="group flex gap-4 rounded-2xl bg-white/[0.04] border border-white/10 p-3 min-w-0 w-full hover:bg-white/[0.08] hover:border-[#2ec9c9]/40 transition-colors">
                      <div className="w-20 h-20 rounded-xl bg-white/10 overflow-hidden shrink-0">
                        {e.image_url && <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-[#2ec9c9]">{timeLabel(e.event_date)}</p>
                        <h3 className="text-white font-semibold truncate group-hover:text-[#2ec9c9] transition-colors">{e.title}</h3>
                        <p className="text-xs text-[#8a97a8] mt-0.5 truncate">{e.business_name}</p>
                        {e.venue_name && <p className="text-xs text-[#8a97a8] mt-1 flex items-center gap-1 truncate"><MapPin size={11} /> {e.venue_name}</p>}
                      </div>
                      <div className="self-center text-right shrink-0">
                        <p className="text-white font-bold text-sm">£{e.from_price.toFixed(2)}</p>
                        {e.multi_price && <p className="text-[10px] text-[#8a97a8]">from</p>}
                        <ArrowRight size={16} className="text-[#8a97a8] group-hover:text-[#2ec9c9] transition-colors ml-auto mt-1" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
